import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { combineClassnames } from 'app/common/presentation'
import {
  ArrowIcon,
  CheckIcon,
  LogoutIcon,
  MailIcon,
  UserIcon,
  UserUploadedIcon,
} from 'app/common/presentation/icons'
import { getInitials } from 'app/common/presentation/initials'
import { useUser } from 'app/user/application'
import { User, Workspace } from 'app/user/types'

import { ActivityIndicator, Avatar } from 'components'

import WorkspaceMenuItem from './MenuItem'
import styles from './Profile.module.scss'

interface WorkspaceMenuProps {
  workspace: Workspace
  user: User
}

type ChangeWorkspaceState = 'changing' | 'error'

const workspaceAvatarClasses = ['one', 'two', 'three']

const WorkspaceMenu = ({ workspace: selectedWorkspace, user }: WorkspaceMenuProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation('header')

  const { changeWorkspace: changeWorkspace, logout } = useUser()

  const [changeWorkspaceState, setChangeWorkspaceState] = useState<
    ChangeWorkspaceState | undefined
  >()

  const activeWorkspaces = user.workspaces.filter((workspace) => workspace.status === 'active')
  const pendingInvitedCount = user.workspaces.filter(
    (workspace) => workspace.status === 'invited',
  ).length

  const handleChangeWorkspace = (id: string) => {
    setChangeWorkspaceState('changing')
    void changeWorkspace(id, async (success) => {
      setChangeWorkspaceState(success ? undefined : 'error')
      if (success) {
        await navigate('/')
      }
    })
  }

  const renderChangeWorkspaceIndicator = () => {
    switch (changeWorkspaceState) {
      case 'changing':
        return <ActivityIndicator size="xxs" />
      case 'error':
        return <span className={styles.error}>{t('profile.menu.changeError')}</span>
      default:
        return undefined
    }
  }

  return (
    <div className={styles.workspaceMenu}>
      <WorkspaceMenuItem
        avatar={<Avatar className={styles.avatar} initials={getInitials(user)} />}
        className={styles.header}
        subtitle={user.email}
        title={selectedWorkspace.name}
      />
      <div className={styles.divider}></div>
      <WorkspaceMenuItem
        leftIcon={UserIcon}
        title={t('profile.menu.userSettings')}
        to={'settings/user'}
      />
      {pendingInvitedCount > 0 && (
        <WorkspaceMenuItem
          count={pendingInvitedCount}
          leftIcon={MailIcon}
          title={t('profile.menu.pendingInvites')}
          to={'settings/user'}
        />
      )}
      {activeWorkspaces.length > 0 && (
        <>
          <div className={styles.divider}></div>
          <WorkspaceMenuItem
            className={styles.section}
            rightElement={renderChangeWorkspaceIndicator()}
            title={t('profile.menu.workspaces')}
          />
          {activeWorkspaces.map((workspace, index) => (
            <WorkspaceMenuItem
              avatar={
                <Avatar
                  className={combineClassnames(
                    styles.avatar,
                    styles[workspaceAvatarClasses[index % 3]],
                  )}
                  initials={getInitials(user)}
                />
              }
              className={styles.header}
              key={workspace.id}
              onClick={
                workspace.id !== selectedWorkspace.id
                  ? () => handleChangeWorkspace(workspace.id)
                  : undefined
              }
              rightElement={{
                active: workspace.id === selectedWorkspace.id ? CheckIcon : undefined,
                hovered: ArrowIcon,
              }}
              subtitle={user.email}
              title={workspace.name}
            />
          ))}
        </>
      )}
      {user.isAdmin && (
        <>
          <div className={styles.divider}></div>
          <WorkspaceMenuItem
            leftIcon={UserUploadedIcon}
            title={t('profile.menu.invite')}
            to={'settings/users'}
          />
        </>
      )}
      <div className={styles.divider}></div>
      <WorkspaceMenuItem
        leftIcon={LogoutIcon}
        title={t('profile.menu.logout')}
        onClick={() => void logout()}
      />
    </div>
  )
}

export default WorkspaceMenu
